import { useState, useEffect, useCallback } from "react";
import { getBidderByUuid } from "services";
import {
  determineKycType,
  findFirstPendingStep,
  getStepIndex,
} from "../utils/kycStepUtils";

interface UseBidderKycDataReturn {
  bidder: any;
  loading: boolean;
  kycMode: string;
  offlinePaymentValidation: boolean;
  offlinePaymentValidationState: string;
  initialStep: number;
  init: (bidderUuid: any) => Promise<void>;
}

export const useBidderKycData = (bidderUuid: any): UseBidderKycDataReturn => {
  const [loading, setLoading] = useState(false);
  const [bidder, setBidder] = useState<any>(null);
  const [initialStep, setInitialStep] = useState(-1);
  const [kycMode, setKycMode] = useState<string>("");
  const [offlinePaymentValidation, setOfflinePaymentValidation] =
    useState(false);
  const [offlinePaymentValidationState, setOfflinePaymentValidationState] =
    useState("pending");

  const init = useCallback(async (bidderUuid: any) => {
    setLoading(true);
    if (!bidderUuid) {
      setLoading(false);
      return;
    }

    try {
      const data: any = await getBidderByUuid(bidderUuid);
      setBidder(data);

      let mode = data?.meta?.bid_application_flow_mode || "";
      if (process.env.NEXT_PUBLIC_ENABLE_OFFLINE_NI_KYC !== "true") {
        mode = "online";
      }
      if (process.env.NEXT_PUBLIC_ENABLE_OFFLINE_NI_KYC !== "true") {
        mode = "online";
      }
      setKycMode(mode);

      // Handle offline payment validation
      if (process.env.NEXT_PUBLIC_ENABLE_ONLINE_DD_VALIDATION_FLOW === "true") {
        if (data?.meta?.enable_offline_payment_validation === true) {
          setOfflinePaymentValidation(true);
          setOfflinePaymentValidationState(
            data?.payment_data?.form_submission === "submitted"
              ? "submitted"
              : "pending",
          );
        }
      }

      // Determine initial step for offline mode
      if (mode === "offline") {
        const kycType = determineKycType(data);
        const accessType = data?.access_type;
        const step = findFirstPendingStep(
          data?.meta?.steps_status,
          kycType,
          mode,
          accessType,
        );
        setInitialStep(getStepIndex(step));
      }
    } catch (error) {
      console.error("Error initializing bidder KYC data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    init(bidderUuid);
  }, [bidderUuid, init]);

  return {
    bidder,
    loading,
    kycMode,
    offlinePaymentValidation,
    offlinePaymentValidationState,
    initialStep,
    init,
  };
};
